import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Users, UserCheck, Activity, CreditCard, TrendingUp, ArrowLeft,
  Download, RefreshCw, BarChart3, Settings, Award,
  Shield, Calendar
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import EncryptionTab from "./EncryptionTab";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const PERIODS = [
  { value: "7d", label: "7 jours" },
  { value: "30d", label: "30 jours" },
  { value: "90d", label: "90 jours" },
  { value: "all", label: "Tout" },
];

// ============ STAT CARD ============
const StatCard = ({ title, value, subtitle, color = "text-primary", testId, ...rest }) => {
  const Icon = rest.icon;
  return (
  <Card data-testid={testId}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">{title}</p>
          <p className={`text-2xl sm:text-3xl font-bold mt-1 ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-muted/50 shrink-0 ml-3">
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
  );
};

const PeriodSelector = ({ period, setPeriod }) => (
  <div className="flex gap-1 bg-muted/50 rounded-lg p-1" data-testid="period-selector">
    {PERIODS.map((p) => (
      <button key={p.value} onClick={() => setPeriod(p.value)}
        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${period === p.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        data-testid={`period-${p.value}`}>{p.label}</button>
    ))}
  </div>
);

// ============ OVERVIEW TAB ============
const OverviewTab = ({ stats, period, setPeriod, userGrowth, connections, usage }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <h2 className="text-lg font-semibold">Vue d'ensemble</h2>
      <PeriodSelector period={period} setPeriod={setPeriod} />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <StatCard title="Utilisateurs" value={stats.total_users} subtitle={`+${stats.new_users} sur la periode`} icon={Users} testId="stat-users" />
      <StatCard title="Therapeutes" value={stats.total_therapists} subtitle={`+${stats.new_therapists} nouveaux`} icon={UserCheck} color="text-emerald-600" testId="stat-therapists" />
      <StatCard title="Patients" value={stats.total_patients} subtitle={`${stats.active_patients} actifs`} icon={Activity} color="text-blue-600" testId="stat-patients" />
      <StatCard title="Abonnements actifs" value={stats.active_subscriptions} subtitle={`${stats.total_subscriptions} total`} icon={CreditCard} color="text-amber-600" testId="stat-subs" />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <StatCard title="Connexions" value={stats.recent_sessions} subtitle="sur la periode" icon={TrendingUp} color="text-violet-600" testId="stat-sessions" />
      <StatCard title="Exercices" value={stats.recent_exercises_completed} subtitle="sur la periode" icon={BarChart3} color="text-rose-600" testId="stat-exercises" />
      <StatCard title="Parrainages" value={stats.total_referrals} subtitle={`${stats.total_referral_rewards} recompenses`} icon={Award} color="text-teal-600" testId="stat-referrals" />
      <StatCard title="Securite" value="2FA" subtitle="Obligatoire therapeutes" icon={Shield} color="text-indigo-600" testId="stat-security" />
    </div>
    {userGrowth.length > 0 && (
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Inscriptions</CardTitle></CardHeader>
        <CardContent><div className="h-64"><ResponsiveContainer width="100%" height="100%">
          <BarChart data={userGrowth}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="therapists" name="Therapeutes" fill="hsl(142, 71%, 45%)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="patients" name="Patients" fill="hsl(217, 91%, 60%)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer></div></CardContent></Card>
    )}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {connections.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Connexions / jour</CardTitle></CardHeader>
          <CardContent><div className="h-48"><ResponsiveContainer width="100%" height="100%">
            <LineChart data={connections}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="connections" stroke="hsl(262, 83%, 58%)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer></div></CardContent></Card>
      )}
      {usage.length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Exercices completes / jour</CardTitle></CardHeader>
          <CardContent><div className="h-48"><ResponsiveContainer width="100%" height="100%">
            <LineChart data={usage}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="exercises" stroke="hsl(350, 89%, 60%)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer></div></CardContent></Card>
      )}
    </div>
  </div>
);

// ============ THERAPISTS TAB ============
const TherapistsTab = ({ therapists, onExport }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-semibold">Therapeutes ({therapists.length})</h2>
      <Button variant="outline" size="sm" onClick={() => onExport("therapists")} data-testid="export-therapists"><Download className="w-4 h-4 mr-1.5" /> CSV</Button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="therapists-table">
        <thead><tr className="border-b text-left">
          <th className="py-3 px-2 font-medium text-muted-foreground">Nom</th>
          <th className="py-3 px-2 font-medium text-muted-foreground hidden sm:table-cell">Email</th>
          <th className="py-3 px-2 font-medium text-muted-foreground text-center">Patients</th>
          <th className="py-3 px-2 font-medium text-muted-foreground text-center hidden sm:table-cell">Actifs</th>
          <th className="py-3 px-2 font-medium text-muted-foreground text-center">Abonnement</th>
          <th className="py-3 px-2 font-medium text-muted-foreground hidden lg:table-cell">Inscrit le</th>
        </tr></thead>
        <tbody>{therapists.map((t) => (
          <tr key={t.user_id} className="border-b hover:bg-muted/30 transition-colors">
            <td className="py-3 px-2 font-medium">{t.name}</td>
            <td className="py-3 px-2 text-muted-foreground hidden sm:table-cell">{t.email}</td>
            <td className="py-3 px-2 text-center font-semibold">{t.total_patients}</td>
            <td className="py-3 px-2 text-center hidden sm:table-cell">{t.active_patients}</td>
            <td className="py-3 px-2 text-center">
              {t.invited_by_owner ? <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium dark:bg-blue-950 dark:text-blue-300">Invite</span>
              : t.subscription?.status === "active" ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium dark:bg-emerald-950 dark:text-emerald-300">Actif</span>
              : t.subscription?.status === "trialing" ? <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium dark:bg-amber-950 dark:text-amber-300">Essai</span>
              : <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium dark:bg-red-950 dark:text-red-300">Aucun</span>}
            </td>
            <td className="py-3 px-2 text-muted-foreground text-xs hidden lg:table-cell">{t.created_at ? new Date(t.created_at).toLocaleDateString("fr-FR") : "-"}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  </div>
);

// ============ REFERRALS TAB ============
const ReferralsTab = ({ referrals }) => (
  <div className="space-y-4">
    <h2 className="text-lg font-semibold">Parrainages</h2>
    {referrals.length === 0 ? <p className="text-muted-foreground text-sm py-8 text-center">Aucun parrainage pour le moment</p> : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="referrals-table">
          <thead><tr className="border-b text-left">
            <th className="py-3 px-2 font-medium text-muted-foreground">Parrain</th>
            <th className="py-3 px-2 font-medium text-muted-foreground hidden sm:table-cell">Code</th>
            <th className="py-3 px-2 font-medium text-muted-foreground text-center">Filleuls</th>
            <th className="py-3 px-2 font-medium text-muted-foreground text-center">Recompenses</th>
          </tr></thead>
          <tbody>{referrals.map((r, i) => (
            <tr key={i} className="border-b hover:bg-muted/30 transition-colors">
              <td className="py-3 px-2"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
              <td className="py-3 px-2 font-mono text-xs hidden sm:table-cell">{r.referral_code}</td>
              <td className="py-3 px-2 text-center font-semibold">{r.referred_count}</td>
              <td className="py-3 px-2 text-center">{r.rewards_earned}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    )}
  </div>
);

// ============ SALES TAB ============
const SalesTab = ({ sales, period, setPeriod }) => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <h2 className="text-lg font-semibold">Ventes & Abonnements</h2>
      <PeriodSelector period={period} setPeriod={setPeriod} />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <StatCard title="Abonnements actifs" value={sales.active || 0} icon={CreditCard} color="text-emerald-600" testId="sales-active" />
      <StatCard title="En essai" value={sales.trialing || 0} icon={Calendar} color="text-amber-600" testId="sales-trial" />
      <StatCard title="Annules" value={sales.canceled || 0} icon={CreditCard} color="text-red-600" testId="sales-canceled" />
      <StatCard title="Revenus periode" value={`${(sales.total_revenue_period || 0).toFixed(0)} EUR`} icon={TrendingUp} color="text-blue-600" testId="sales-revenue" />
    </div>
    {sales.revenue_data?.length > 0 && (
      <Card><CardHeader className="pb-2"><CardTitle className="text-base">Revenus / jour (EUR)</CardTitle></CardHeader>
        <CardContent><div className="h-56"><ResponsiveContainer width="100%" height="100%">
          <BarChart data={sales.revenue_data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fontSize: 10 }} /><Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="revenue" name="Revenus (EUR)" fill="hsl(217, 91%, 60%)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer></div></CardContent></Card>
    )}
  </div>
);


// ============ MAIN ADMIN DASHBOARD ============

// ============ MAIN ADMIN DASHBOARD ============
const TABS = [
  { id: "overview", label: "Vue d'ensemble", icon: BarChart3 },
  { id: "therapists", label: "Therapeutes", icon: UserCheck },
  { id: "referrals", label: "Parrainages", icon: Award },
  { id: "sales", label: "Ventes", icon: CreditCard },
  { id: "encryption", label: "Chiffrement", icon: Shield },
];

export default function AdminDashboard({ user, logout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({});
  const [userGrowth, setUserGrowth] = useState([]);
  const [connections, setConnections] = useState([]);
  const [usage, setUsage] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [sales, setSales] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, growthRes, connRes, usageRes] = await Promise.all([
        axios.get(`${API}/admin/stats/overview?period=${period}`),
        axios.get(`${API}/admin/stats/users?period=${period}`),
        axios.get(`${API}/admin/stats/connections?period=${period}`),
        axios.get(`${API}/admin/stats/usage?period=${period}`),
      ]);
      setStats(statsRes.data);
      setUserGrowth(growthRes.data);
      setConnections(connRes.data);
      setUsage(usageRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Acces refuse — admin uniquement");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  }, [period, navigate]);

  const loadTabData = useCallback(async () => {
    try {
      if (activeTab === "therapists" && therapists.length === 0) {
        const res = await axios.get(`${API}/admin/stats/therapists`);
        setTherapists(res.data);
      } else if (activeTab === "referrals" && referrals.length === 0) {
        const res = await axios.get(`${API}/admin/stats/referrals`);
        setReferrals(res.data);
      } else if (activeTab === "sales") {
        const res = await axios.get(`${API}/admin/stats/sales?period=${period}`);
        setSales(res.data);
      }
    } catch (err) {
      console.error("Tab data error:", err);
    }
  }, [activeTab, period, therapists.length, referrals.length]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadTabData(); }, [loadTabData]);

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${type}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export CSV telecharge");
    } catch (err) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleRefresh = () => {
    setTherapists([]);
    setReferrals([]);
    loadData();
    toast.success("Donnees actualisees");
  };

  if (loading && !stats.total_users) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20" />
          <p className="text-muted-foreground text-sm">Chargement du tableau de bord admin...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="admin-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2"><Settings className="w-5 h-5 text-primary" /> Administration</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">Kintsugi TCC — Panneau proprietaire</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="admin-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => handleExport("users")} data-testid="admin-export-users">
              <Download className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="border-b bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1 overflow-x-auto py-2 -mb-px">
            {TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${
                  activeTab === tab.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
                data-testid={`admin-tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === "overview" && <OverviewTab stats={stats} period={period} setPeriod={setPeriod} userGrowth={userGrowth} connections={connections} usage={usage} />}
        {activeTab === "therapists" && <TherapistsTab therapists={therapists} onExport={handleExport} />}
        {activeTab === "referrals" && <ReferralsTab referrals={referrals} />}
        {activeTab === "sales" && <SalesTab sales={sales} period={period} setPeriod={setPeriod} />}
        {activeTab === "encryption" && <EncryptionTab />}
      </div>
    </div>
  );
}
